import { config } from '../../config';
import type { DiscoveryHypothesisView } from '../../app/discovery';
import { usePointerGesture } from '../../ui/capture/usePointerGesture';
import { PhaseIcon } from '../PhaseIcon';
import type { DiscoveryGesturesB } from './useDiscoveryB';

/**
 * B-9 — one Discovery row (design spec §2.4): *"Row anatomy (≥60 pt): phase icon, hypothesis
 * text (15–17 px), count ring + numeral (dominant, tabular), whole row is the increment
 * target."* Scenario 8's single-interaction record with a peripherally-visible ack.
 *
 * The three data attributes are A's proven contract, unchanged in meaning:
 *
 * | Attribute | Load-bearing for |
 * |---|---|
 * | `data-hid` | the hypothesis, resolved at `pointerdown` — DISC-7's correctness layer: no reorder can retarget a tap that has begun. |
 * | `data-ack` | scenario 8's ack — toggled **imperatively** in the commit moment, never React state (a re-render is a frame away; the ack lands *at* the commit). |
 * | `data-pressed` | receipt of touch within one frame of `pointerdown` (skill §2) — CSS only, no data claim. |
 *
 * `data-role="tile"` stands even though B-9 renders no second hit region: B-11's undo pill
 * lives in the bottom band (not inset in the row, unlike A), but the region contract costs
 * nothing and keeps `resolveTarget` semantics identical across A and B surfaces.
 *
 * Status is never hue-only (§2.3): confirmed pairs green with a **filled** ring + heavier row
 * border; disproved pairs red with a strike on the title; invalidated drops the row's opacity.
 * All carried by `data-status` on the row so the whole row is the signal.
 *
 * Long-press opens B-15's **hypothesis detail sheet** (annotate, voice note, status override).
 * The hold fires from the gesture layer's own timer while the finger is still down, and the
 * contact then classifies as `longPress` rather than `tap` — so the press that opens the sheet
 * never also increments (the same contract A's hold-to-talk rides).
 */
export interface DiscoveryRowBProps {
  readonly row: DiscoveryHypothesisView;
  readonly gestures: DiscoveryGesturesB;
  /** `false` before kickoff: the row renders, contacts are declined at `onPressStart`. */
  readonly capturing: boolean;
}

export function DiscoveryRowB({ row, gestures, capturing }: DiscoveryRowBProps) {
  const handlers = usePointerGesture({
    onPressStart: gestures.onPressStart,
    onTap: gestures.onTap,
    onLongPress: gestures.onLongPress,
    onPressEnd: gestures.onPressEnd,
    onActiveChange: gestures.onActiveChange,
    // Swipe gestures belong to the locked board (B-10). Nothing horizontal lives here — §2.4
    // chose tap-switch segments over swipe paging precisely to keep this axis meaningless.
    swipeLeft: false,
  });

  return (
    <li className="discb-row__host">
      <div
        className="discb-row"
        data-hid={row.id}
        data-role="tile"
        data-status={row.status}
        data-capturing={capturing ? 'true' : 'false'}
        data-testid={`discb-row-${row.id}`}
        style={{ minHeight: `${String(config.MIN_TOUCH_TARGET_PT)}px` }}
        {...handlers}
      >
        <PhaseIcon phase={row.phase} />
        <span className="discb-row__text">{row.text}</span>
        {/* The dominant numeral (skill §2/§11): one count, tabular, inside its ring. The ring
            is the second channel of the confirmed signal (fill + weight, not hue alone). */}
        <span className="discb-count" data-testid={`discb-count-${row.id}`}>
          {row.cachedCount}
        </span>
      </div>
    </li>
  );
}
